import { Component, OnInit, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Router } from '@angular/router';


interface MenuItem {
  label: string;
  action?: string; // for add delete edit button
  children?: MenuItem[];
  path?: string;
  expanded?: boolean; // Track expanded state
}

@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [CommonModule, HttpClientModule],
  templateUrl: './landing-page.component.html',
  styleUrl: './landing-page.component.css'
})
export class LandingPageComponent implements OnInit {

  menu: MenuItem[] = [];

  constructor(private http: HttpClient, private elementRef: ElementRef, private router: Router) { }

  ngOnInit() {
    this.http
      .get<MenuItem[]>('assets/landing-page-config/landing_page.json')
      .subscribe({
        next: (data) => {
          this.menu = data;
          // Initialize expanded property for all nodes
          this.initializeMenuItems(this.menu);
        },
        error: (err) => {
          console.error('Error loading JSON:', err);
        }
      });
  }

  // Initialize all menu items with expanded property
  private initializeMenuItems(items: MenuItem[]) {
    items.forEach(item => {
      item.expanded = false;
      if (item.children) {
        this.initializeMenuItems(item.children);
      }
    });
  }

  navigateToPath(path: string) {
    if (path) {
      this.router.navigate([path]); // Navigate to the provided path
    }
  }


  // Toggle card header visibility - only one card expanded at a time
  toggleCard(cardIndex: number, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.menu && this.menu[cardIndex]) {
      // Check if current card is expanded
      const isCurrentExpanded = this.menu[cardIndex].expanded;

      // Close all other cards
      this.menu.forEach((item, index) => {
        if (index !== cardIndex) {
          item.expanded = false;
        }
      });

      // Toggle current card (if it was expanded, close it; if collapsed, open it)
      this.menu[cardIndex].expanded = !isCurrentExpanded;
    }
  }

  // Expand card on hover - mutually exclusive
  expandCard(cardIndex: number, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.menu && this.menu[cardIndex]) {
      // Close all other cards
      this.menu.forEach((item, index) => {
        if (index !== cardIndex) {
          item.expanded = false;
        }
      });

      // Open the hovered card
      this.menu[cardIndex].expanded = true;
    }
  }

  // Close all cards when clicking outside
  @HostListener('document:click', ['$event'])
  closeDropdownOnOutsideClick(event: Event) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);

    if (!clickedInside) {
      // Close all cards
      this.menu.forEach(item => {
        item.expanded = false;
      });
    }
  }

  // Toggle nested menu items - close siblings
  toggleNode(node: MenuItem, siblings: MenuItem[], event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (node.children) {
      // Close all sibling nodes that have children
      siblings.forEach(sibling => {
        if (sibling !== node && sibling.children) {
          sibling.expanded = false;
        }
      });

      // Toggle current node
      node.expanded = !node.expanded;
    }
  }

  handleNodeClick(node: MenuItem, siblings: MenuItem[], event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (node.children && node.children.length > 0) {
      siblings.forEach((sibling) => {
        if (sibling !== node && sibling.children) {
          sibling.expanded = false;
        }
      });
      node.expanded = !node.expanded;
      return;
    }

    if (node.path) {
      this.navigateToPath(node.path);
    }
  }

  // Expand nested menu items on hover - mutually exclusive with siblings
  expandNode(node: MenuItem, siblings: MenuItem[], event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (node.children) {
      // Close all sibling nodes that have children
      siblings.forEach(sibling => {
        if (sibling !== node && sibling.children) {
          sibling.expanded = false;
        }
      });

      // Open the hovered node
      node.expanded = true;
    }
  }

  hasNavigablePath(node: MenuItem): boolean {
    if (node.path) {
      return true;
    }
    if (!node.children || node.children.length === 0) {
      return false;
    }
    return node.children.some((child) => this.hasNavigablePath(child));
  }


}



